// Game SFX synthesis. One-shots use short layered envelopes; continuous
// effects expose held/released voices so the tuner and game can stop them
// deliberately instead of relying on fixed-duration previews.

import { airBrakeLevelForSpeed } from './audio-signals';

export type ScrapeSurface = 'track' | 'dirt' | 'wall';

export interface SfxConfig {
  backfireGain: number;
  boostGain: number;
  slipRaspGain: number;
  kerbGain: number;
  thunkGain: number;
  scrapeGain: number;
  airBrakeGain: number;
  igniteGain: number;
  backfire: {
    filterBaseHz: number;
    filterSweepHz: number;
    filterQ: number;
    attackMs: number;
    decayMs: number;
  };
  boost: {
    attackMs: number;
    releaseMs: number;
    noiseGain: number;
    whineGain: number;
    rumbleGain: number;
    spoolStartHz: number;
    spoolEndHz: number;
    whineStartHz: number;
    whineEndHz: number;
    rumbleHz: number;
  };
  slipRasp: {
    attackMs: number;
    releaseMs: number;
    noiseGain: number;
    whineGain: number;
    bodyGain: number;
    filterBaseHz: number;
    filterSweepHz: number;
    filterQ: number;
    whineBaseHz: number;
    whineSweepHz: number;
    bodyHz: number;
    wobbleDepth: number;
  };
  kerb: {
    frequencyBaseHz: number;
    frequencySweepHz: number;
    attackMs: number;
    decayMs: number;
    speedGainMin: number;
    speedGainMax: number;
    bodyHz: number;
    bodyMix: number;
    clackMix: number;
  };
  thunk: {
    noiseFilterBaseHz: number;
    noiseFilterSweepHz: number;
    noiseFilterQ: number;
    noiseDecayMs: number;
    bonkBaseHz: number;
    bonkSweepHz: number;
    bonkDecayMs: number;
    bonkMix: number;
    subHz: number;
    subMix: number;
    crunchMix: number;
    rattleDecayMs: number;
  };
  scrape: {
    intensityScale: number;
    speedScale: number;
    roughness: number;
    surface: ScrapeSurface;
    grindMix: number;
    sparkMix: number;
    modalMix: number;
    drive: number;
    sparkRateMin: number;
    sparkRateMax: number;
    modalFundamental: number;
  };
  airBrake: {
    highpassStartHz: number;
    sustainHighpassHz: number;
    highpassEndHz: number;
    filterQ: number;
    attackMs: number;
    releaseMs: number;
    decayMs: number;
    sustainGain: number;
  };
  ignite: {
    starterStartHz: number;
    starterEndHz: number;
    starterGain: number;
    starterMs: number;
    whomphStartHz: number;
    whomphEndHz: number;
    whomphGain: number;
    popStartHz: number;
    popEndHz: number;
    popGain: number;
    bodyMs: number;
    spoolStartHz: number;
    spoolEndHz: number;
    spoolGain: number;
    spoolMs: number;
  };
}

export const DEFAULT_SFX: SfxConfig = {
  backfireGain: 0.6,
  boostGain: 0.78,
  slipRaspGain: 0.58,
  kerbGain: 0.72,
  thunkGain: 0.92,
  scrapeGain: 0.72,
  airBrakeGain: 0.7,
  igniteGain: 0.85,
  backfire: {
    filterBaseHz: 240,
    filterSweepHz: 600,
    filterQ: 1.4,
    attackMs: 5,
    decayMs: 180,
  },
  boost: {
    attackMs: 180,
    releaseMs: 420,
    noiseGain: 0.72,
    whineGain: 0.55,
    rumbleGain: 0.28,
    spoolStartHz: 520,
    spoolEndHz: 2800,
    whineStartHz: 900,
    whineEndHz: 5200,
    rumbleHz: 72,
  },
  slipRasp: {
    attackMs: 35,
    releaseMs: 180,
    noiseGain: 0.7,
    whineGain: 0.34,
    bodyGain: 0.38,
    filterBaseHz: 980,
    filterSweepHz: 2600,
    filterQ: 1.8,
    whineBaseHz: 760,
    whineSweepHz: 2900,
    bodyHz: 118,
    wobbleDepth: 0.16,
  },
  kerb: {
    frequencyBaseHz: 210,
    frequencySweepHz: 320,
    attackMs: 3,
    decayMs: 130,
    speedGainMin: 0.55,
    speedGainMax: 1.35,
    bodyHz: 82,
    bodyMix: 0.82,
    clackMix: 0.38,
  },
  thunk: {
    noiseFilterBaseHz: 130,
    noiseFilterSweepHz: 900,
    noiseFilterQ: 0.55,
    noiseDecayMs: 760,
    bonkBaseHz: 92,
    bonkSweepHz: 48,
    bonkDecayMs: 680,
    bonkMix: 0.26,
    subHz: 42,
    subMix: 0.98,
    crunchMix: 0.85,
    rattleDecayMs: 1180,
  },
  scrape: {
    intensityScale: 1.16,
    speedScale: 1,
    roughness: 0.76,
    surface: 'track',
    grindMix: 1.12,
    sparkMix: 1.1,
    modalMix: 0.92,
    drive: 0.52,
    sparkRateMin: 45,
    sparkRateMax: 820,
    modalFundamental: 260,
  },
  airBrake: {
    highpassStartHz: 4200,
    sustainHighpassHz: 1700,
    highpassEndHz: 520,
    filterQ: 0.7,
    attackMs: 70,
    releaseMs: 260,
    decayMs: 650,
    sustainGain: 0.88,
  },
  ignite: {
    starterStartHz: 720,
    starterEndHz: 310,
    starterGain: 0.5,
    starterMs: 980,
    whomphStartHz: 420,
    whomphEndHz: 62,
    whomphGain: 0.9,
    popStartHz: 96,
    popEndHz: 26,
    popGain: 0.62,
    bodyMs: 1260,
    spoolStartHz: 220,
    spoolEndHz: 3800,
    spoolGain: 0.62,
    spoolMs: 2300,
  },
};

export class Sfx {
  private noiseBuffer: AudioBuffer | null = null;
  private scrape: ScrapeVoice | null = null;
  private boost: BoostVoice | null = null;
  private slipRasp: SlipRaspVoice | null = null;
  private airBrake: AirBrakeVoice | null = null;
  private ignitionSources: AudioScheduledSourceNode[] = [];
  private scrapeReady = false;
  private scrapeLoadPromise: Promise<void> | null = null;

  constructor(
    private readonly ctx: AudioContext,
    private readonly destination: AudioNode,
    public config: SfxConfig = { ...DEFAULT_SFX },
  ) {}

  private getNoise(): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const len = Math.floor(this.ctx.sampleRate * 0.5);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;
    return buf;
  }

  // Throttle-release pop. Punchy lowpass-noise burst with a short body.
  backfire(intensity = 1): void {
    const t = this.ctx.currentTime;
    const cfg = this.config.backfire;
    const src = this.ctx.createBufferSource();
    src.buffer = this.getNoise();
    src.loop = false;

    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = cfg.filterBaseHz + cfg.filterSweepHz * intensity;
    lp.Q.value = cfg.filterQ;

    const gain = this.ctx.createGain();
    const attack = ms(cfg.attackMs);
    const decay = ms(cfg.decayMs);
    gain.gain.value = 0;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(this.config.backfireGain * intensity, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, t + decay);

    src.connect(lp).connect(gain).connect(this.destination);
    src.start(t);
    src.stop(t + decay + 0.04);
  }

  // Kerb contact — low body clunk plus a short hard edge. Speed changes
  // force and repetition in the caller; this voice is one ridge impact.
  kerbTick(speed01: number): void {
    const t = this.ctx.currentTime;
    const cfg = this.config.kerb;
    const intensity = this.config.kerbGain * (cfg.speedGainMin + speed01 * (cfg.speedGainMax - cfg.speedGainMin));

    const body = this.ctx.createOscillator();
    body.type = 'triangle';
    body.frequency.setValueAtTime(cfg.bodyHz + speed01 * 24, t);
    body.frequency.exponentialRampToValueAtTime(Math.max(32, cfg.bodyHz * 0.65), t + ms(cfg.decayMs) * 0.82);
    const bodyGain = this.ctx.createGain();
    bodyGain.gain.setValueAtTime(0, t);
    bodyGain.gain.linearRampToValueAtTime(intensity * cfg.bodyMix, t + ms(cfg.attackMs));
    bodyGain.gain.exponentialRampToValueAtTime(0.001, t + ms(cfg.decayMs));
    body.connect(bodyGain).connect(this.destination);
    body.start(t);
    body.stop(t + ms(cfg.decayMs) + 0.04);

    const src = this.ctx.createBufferSource();
    src.buffer = this.getNoise();
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = cfg.frequencyBaseHz + speed01 * cfg.frequencySweepHz;
    bp.Q.value = 1.1;

    const gain = this.ctx.createGain();
    const attack = ms(cfg.attackMs);
    const decay = ms(cfg.decayMs);
    gain.gain.value = 0;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(intensity * cfg.clackMix, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, t + decay);

    src.connect(bp).connect(gain).connect(this.destination);
    src.start(t);
    src.stop(t + decay + 0.02);
  }

  // Wall hit — force-scaled crash transient only. Continuous wall contact
  // is handled by setScrape() so the game can transition crash → scrape.
  wallThunk(impactSpeed01: number): void {
    const t = this.ctx.currentTime;
    const cfg = this.config.thunk;
    const impact = clamp01(impactSpeed01);
    const force = Math.pow(Math.max(0.04, impact), 0.72);
    const master = this.config.thunkGain;

    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(Math.max(24, cfg.subHz + force * 10), t);
    sub.frequency.exponentialRampToValueAtTime(Math.max(22, cfg.subHz * 0.48), t + 0.34 + force * 0.22);
    const subGain = this.ctx.createGain();
    subGain.gain.setValueAtTime(0, t);
    subGain.gain.linearRampToValueAtTime(master * cfg.subMix * (0.14 + force * 0.68), t + 0.018);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.46 + force * 0.28);
    sub.connect(subGain).connect(this.destination);
    sub.start(t);
    sub.stop(t + 0.82);

    const src = this.ctx.createBufferSource();
    src.buffer = this.getNoise();
    const noiseDecay = ms(cfg.noiseDecayMs) * (0.42 + force * 1.05);
    src.loop = noiseDecay > 0.48;

    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(cfg.noiseFilterBaseHz + cfg.noiseFilterSweepHz * (0.25 + force * 0.75), t);
    lp.frequency.exponentialRampToValueAtTime(Math.max(70, cfg.noiseFilterBaseHz * 0.72), t + noiseDecay * 0.82);
    lp.Q.value = cfg.noiseFilterQ;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0, t);
    noiseGain.gain.linearRampToValueAtTime(master * (0.18 + force * 0.58), t + 0.018);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + noiseDecay);

    src.connect(lp).connect(noiseGain).connect(this.destination);
    src.start(t);
    src.stop(t + noiseDecay + 0.08);

    // Low barrier/body resonance. Kept soft so it bends instead of slaps.
    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(Math.max(34, cfg.bonkBaseHz + cfg.bonkSweepHz * force), t + 0.012);
    osc.frequency.exponentialRampToValueAtTime(Math.max(28, cfg.bonkBaseHz * 0.52), t + 0.26 + force * 0.2);
    const oscGain = this.ctx.createGain();
    const bonkDecay = ms(cfg.bonkDecayMs) * (0.45 + force * 0.75);
    oscGain.gain.setValueAtTime(0, t + 0.012);
    oscGain.gain.linearRampToValueAtTime(master * cfg.bonkMix * force, t + 0.035);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + bonkDecay);
    osc.connect(oscGain).connect(this.destination);
    osc.start(t + 0.012);
    osc.stop(t + bonkDecay + 0.02);

    const rattleCount = Math.max(3, Math.round(3 + force * 9));
    const rattleSpan = ms(cfg.rattleDecayMs) * (0.18 + force * 0.5);
    for (let i = 0; i < rattleCount; i += 1) {
      const hitTime = t + 0.055 + (i / rattleCount) * rattleSpan;
      const decay = 0.035 + force * 0.035 + (i % 3) * 0.008;
      const falloff = 1 - i / rattleCount;
      const rattle = this.ctx.createBufferSource();
      rattle.buffer = this.getNoise();
      const bp = this.ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 820 + force * 1100 + ((i * 379) % 700);
      bp.Q.value = 2.1;
      const rattleGain = this.ctx.createGain();
      rattleGain.gain.setValueAtTime(0, hitTime);
      rattleGain.gain.linearRampToValueAtTime(master * cfg.crunchMix * (0.045 + force * 0.08) * falloff, hitTime + 0.006);
      rattleGain.gain.exponentialRampToValueAtTime(0.001, hitTime + decay);
      rattle.connect(bp).connect(rattleGain).connect(this.destination);
      rattle.start(hitTime);
      rattle.stop(hitTime + decay + 0.03);
    }
  }

  // Async-loads the scrape AudioWorklet module. Call once after the
  // AudioContext is started; safe to call multiple times.
  async prepareScrape(): Promise<void> {
    if (this.scrapeReady) return;
    if (!this.scrapeLoadPromise) {
      this.scrapeLoadPromise = this.ctx.audioWorklet
        .addModule('/audio/scrape-worklet.js')
        .then(() => { this.scrapeReady = true; });
    }
    await this.scrapeLoadPromise;
  }

  // Continuous metallic scrape with sparks. Drive from physics each frame.
  //   intensity:    0 = silent, 1 = full corner-skid
  //   speedHint:    0–1, brightens grind and ups spark rate
  //   roughness:    0–1, jitter of spark Poisson timing
  setScrape(intensity: number, speedHint = 0.5, roughness?: number, surface?: ScrapeSurface): void {
    if (!this.scrapeReady) {
      // Silently no-op until the worklet finishes loading. Caller should
      // have awaited prepareScrape() in init.
      if (!this.scrapeLoadPromise) void this.prepareScrape();
      return;
    }
    if (!this.scrape) {
      this.scrape = new ScrapeVoice(this.ctx, this.destination);
    }
    this.scrape.setParams(
      intensity * this.config.scrapeGain * this.config.scrape.intensityScale,
      speedHint * this.config.scrape.speedScale,
      roughness ?? this.config.scrape.roughness,
      scrapeWorkletParams(this.config.scrape, surface ?? this.config.scrape.surface),
    );
  }

  stopScrape(): void {
    if (this.scrape) {
      this.scrape.setParams(0, 0.5, 0.5);
    }
  }

  dispose(): void {
    this.stopIgnition();
    if (this.scrape) {
      this.scrape.dispose();
      this.scrape = null;
    }
    if (this.boost) {
      this.boost.dispose();
      this.boost = null;
    }
    if (this.slipRasp) {
      this.slipRasp.dispose();
      this.slipRasp = null;
    }
    if (this.airBrake) {
      this.airBrake.dispose();
      this.airBrake = null;
    }
    this.noiseBuffer = null;
  }

  setBoost(intensity: number): void {
    const level = clamp01(intensity);
    if (level <= 0.001) {
      this.stopBoost();
      return;
    }
    if (!this.boost) {
      this.boost = new BoostVoice(this.ctx, this.destination, this.getNoise());
    }
    this.boost.set(level, this.config.boost, this.config.boostGain);
  }

  stopBoost(): void {
    if (!this.boost) return;
    const voice = this.boost;
    this.boost = null;
    voice.release(this.config.boost.releaseMs);
  }

  stopBoostFast(): void {
    if (!this.boost) return;
    const voice = this.boost;
    this.boost = null;
    voice.release(60);
  }

  setSlipRasp(intensity: number, speedHint = 0.5): void {
    const level = clamp01(intensity);
    if (level <= 0.001) {
      this.stopSlipRasp();
      return;
    }
    if (!this.slipRasp) {
      this.slipRasp = new SlipRaspVoice(this.ctx, this.destination, this.getNoise());
    }
    this.slipRasp.set(level, clamp01(speedHint), this.config.slipRasp, this.config.slipRaspGain);
  }

  stopSlipRasp(): void {
    if (!this.slipRasp) return;
    const voice = this.slipRasp;
    this.slipRasp = null;
    voice.release(this.config.slipRasp.releaseMs);
  }

  setAirBrake(intensity: number, speedHint = 1): void {
    const level = airBrakeLevelForSpeed(intensity, speedHint);
    if (level <= 0.001) {
      this.stopAirBrake();
      return;
    }
    if (!this.airBrake) {
      this.airBrake = new AirBrakeVoice(this.ctx, this.destination, this.getNoise());
    }
    this.airBrake.set(level, this.config.airBrake, this.config.airBrakeGain);
  }

  stopAirBrake(): void {
    if (!this.airBrake) return;
    const voice = this.airBrake;
    this.airBrake = null;
    voice.release(this.config.airBrake.releaseMs);
  }

  stopIgnition(): void {
    const sources = this.ignitionSources.splice(0);
    for (const source of sources) {
      try {
        source.stop();
      } catch {
        // Source may already have ended.
      }
    }
  }

  private trackIgnition<T extends AudioScheduledSourceNode>(source: T): T {
    this.ignitionSources.push(source);
    source.addEventListener('ended', () => {
      const index = this.ignitionSources.indexOf(source);
      if (index >= 0) this.ignitionSources.splice(index, 1);
    }, { once: true });
    return source;
  }

  // Engine ignition — starter chatter → crank pulses → combustion catch → turbine spool.
  // Returns the wall-clock duration (seconds) so the caller can ramp the
  // engine synth's master gain in over the same window.
  ignite(intensity = 1): number {
    this.stopIgnition();
    const t = this.ctx.currentTime;
    const g = this.config.igniteGain * Math.max(0, intensity);
    const cfg = this.config.ignite;
    const starterDuration = ms(cfg.starterMs);
    const bodyDuration = ms(cfg.bodyMs);
    const spoolDuration = ms(cfg.spoolMs);
    const catchTime = t + Math.min(starterDuration * 0.72, Math.max(0.18, starterDuration - 0.14));
    const hz = (value: number) => Math.max(20, value);

    const noiseLayer = (
      start: number,
      duration: number,
      type: BiquadFilterType,
      fromHz: number,
      toHz: number,
      q: number,
      peak: number,
      attack = 0.015,
      loop = duration > 0.45,
    ) => {
      const source = this.trackIgnition(this.ctx.createBufferSource());
      source.buffer = this.getNoise();
      source.loop = loop;
      const filter = this.ctx.createBiquadFilter();
      filter.type = type;
      filter.frequency.setValueAtTime(hz(fromHz), start);
      filter.frequency.exponentialRampToValueAtTime(hz(toHz), start + Math.max(0.02, duration * 0.86));
      filter.Q.value = q;
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(Math.max(0.0001, peak), start + Math.min(attack, duration * 0.45));
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      source.connect(filter).connect(gain).connect(this.destination);
      source.start(start);
      source.stop(start + duration + 0.03);
    };

    const oscLayer = (
      start: number,
      duration: number,
      type: OscillatorType,
      fromHz: number,
      toHz: number,
      peak: number,
      attack = 0.018,
    ) => {
      const osc = this.trackIgnition(this.ctx.createOscillator());
      osc.type = type;
      osc.frequency.setValueAtTime(hz(fromHz), start);
      osc.frequency.exponentialRampToValueAtTime(hz(toHz), start + Math.max(0.02, duration * 0.9));
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(Math.max(0.0001, peak), start + Math.min(attack, duration * 0.45));
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      osc.connect(gain).connect(this.destination);
      osc.start(start);
      osc.stop(start + duration + 0.03);
    };

    // Starter motor body: a sagging saw plus noisy mechanical whirr.
    const starterOsc = this.trackIgnition(this.ctx.createOscillator());
    starterOsc.type = 'sawtooth';
    starterOsc.frequency.setValueAtTime(hz(cfg.starterStartHz), t);
    starterOsc.frequency.exponentialRampToValueAtTime(hz(cfg.starterEndHz), t + starterDuration * 0.92);
    const starterFilter = this.ctx.createBiquadFilter();
    starterFilter.type = 'bandpass';
    starterFilter.frequency.setValueAtTime(hz(cfg.starterStartHz * 1.18), t);
    starterFilter.frequency.exponentialRampToValueAtTime(hz(cfg.starterEndHz * 1.04), t + starterDuration * 0.92);
    starterFilter.Q.value = 2.8;
    const starterGain = this.ctx.createGain();
    starterGain.gain.setValueAtTime(0.0001, t);
    starterGain.gain.linearRampToValueAtTime(g * cfg.starterGain * 0.2, t + 0.035);
    starterGain.gain.exponentialRampToValueAtTime(0.001, t + starterDuration);
    starterOsc.connect(starterFilter).connect(starterGain).connect(this.destination);
    starterOsc.start(t);
    starterOsc.stop(t + starterDuration + 0.03);
    noiseLayer(t, starterDuration, 'bandpass', cfg.starterStartHz * 0.95, cfg.starterEndHz * 1.15, 4.2, g * cfg.starterGain * 0.15, 0.025);

    // Solenoid / gear chatter before the engine catches.
    const clickCount = Math.max(5, Math.floor(starterDuration / 0.075));
    for (let i = 0; i < clickCount; i += 1) {
      const clickTime = t + 0.025 + (i / clickCount) * starterDuration * 0.78;
      if (clickTime > catchTime - 0.035) break;
      const variance = 0.78 + ((i * 37) % 19) / 60;
      noiseLayer(clickTime, 0.026 + (i % 3) * 0.008, 'bandpass', 3200 * variance, 980 * variance, 3.8, g * cfg.starterGain * 0.12 * variance, 0.002, false);
    }

    // Slow low crank pulses give the startup its chug before combustion.
    const crankCount = Math.max(4, Math.floor(starterDuration / 0.145));
    const crankSpan = Math.max(0.22, catchTime - t - 0.08);
    for (let i = 0; i < crankCount; i += 1) {
      const crankTime = t + 0.08 + (i / Math.max(1, crankCount - 1)) * crankSpan;
      const pitchSag = 1 - Math.min(0.22, i * 0.025);
      const pulseDuration = 0.14 + (i % 2) * 0.025;
      const subStart = Math.max(34, cfg.popStartHz * 0.58 * pitchSag);
      const subEnd = Math.max(22, cfg.popEndHz * 1.08);
      oscLayer(crankTime, pulseDuration, 'sine', subStart, subEnd, g * cfg.popGain * 0.34, 0.018);
      oscLayer(crankTime + 0.012, pulseDuration * 0.8, 'triangle', subStart * 1.7, Math.max(30, subEnd * 1.8), g * cfg.popGain * 0.13, 0.012);
      noiseLayer(crankTime + 0.012, pulseDuration * 0.78, 'lowpass', cfg.whomphStartHz * 0.72, cfg.whomphEndHz * 2.4, 0.7, g * cfg.whomphGain * 0.16, 0.012, false);
    }

    // Combustion catch: low air/body pressure with a few uneven coughs.
    const bodyStart = catchTime - 0.025;
    noiseLayer(bodyStart, bodyDuration, 'lowpass', cfg.whomphStartHz, cfg.whomphEndHz, 0.75, g * cfg.whomphGain * 0.68, 0.035, true);
    oscLayer(bodyStart, bodyDuration * 0.74, 'sine', cfg.popStartHz, cfg.popEndHz, g * cfg.popGain * 0.48, 0.028);
    [0, 0.14, 0.32].forEach((offset, index) => {
      const coughTime = catchTime + offset;
      const level = 1 - index * 0.22;
      noiseLayer(coughTime, 0.14 + index * 0.035, 'bandpass', cfg.whomphStartHz * (1.2 + index * 0.18), cfg.whomphEndHz * (5.2 - index), 1.2, g * cfg.whomphGain * 0.26 * level, 0.01, false);
      oscLayer(coughTime, 0.12, 'triangle', cfg.popStartHz * (0.82 - index * 0.08), cfg.popEndHz * (1.45 - index * 0.14), g * cfg.popGain * 0.2 * level, 0.01);
    });

    // Jet/turbine handoff. These loop the noise buffer so the programmed
    // envelope actually lasts for the full spool duration.
    const spoolStart = catchTime - 0.045;
    noiseLayer(spoolStart, spoolDuration, 'bandpass', cfg.spoolStartHz, cfg.spoolEndHz, 0.85, g * cfg.spoolGain * 0.42, spoolDuration * 0.24, true);
    noiseLayer(spoolStart + 0.08, spoolDuration * 0.86, 'highpass', 340, 1450, 0.55, g * cfg.spoolGain * 0.16, spoolDuration * 0.22, true);
    oscLayer(spoolStart + 0.04, spoolDuration * 0.9, 'sine', cfg.spoolStartHz * 1.55, cfg.spoolEndHz * 0.92, g * cfg.spoolGain * 0.11, spoolDuration * 0.3);

    return Math.max(starterDuration, catchTime - t + bodyDuration, catchTime - t + spoolDuration) + 0.12;
  }

}

function ms(value: number): number {
  return Math.max(0.001, value / 1000);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

// Wraps an AudioWorkletNode running scrape-worklet.js. The DSP — Poisson
// spark scheduler, modal resonator bank, bandpassed brown-noise grind —
// lives in the worklet; this class is a thin parameter-setter for the
// main thread.
class ScrapeVoice {
  private node: AudioWorkletNode;

  constructor(ctx: AudioContext, dest: AudioNode) {
    this.node = new AudioWorkletNode(ctx, 'scrape-pod', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.node.connect(dest);
  }

  setParams(intensity: number, speed: number, roughness: number, workletParams?: ScrapeWorkletParams): void {
    const ctx = this.node.context;
    const t = ctx.currentTime;
    const set = (name: string, v: number): void => {
      const p = this.node.parameters.get(name);
      if (p) p.setTargetAtTime(v, t, 0.04);
    };
    set('intensity', Math.max(0, Math.min(1, intensity)));
    set('speed', Math.max(0, Math.min(1, speed)));
    set('roughness', Math.max(0, Math.min(1, roughness)));
    if (!workletParams) return;
    set('mixGrind', workletParams.mixGrind);
    set('mixSparks', workletParams.mixSparks);
    set('mixModal', workletParams.mixModal);
    set('drive', workletParams.drive);
    set('sparkRateMin', workletParams.sparkRateMin);
    set('sparkRateMax', workletParams.sparkRateMax);
    set('modalFundamental', workletParams.modalFundamental);
  }

  dispose(): void {
    try {
      this.node.port.close();
    } catch {
      // Port may already be closed.
    }
    disconnectNode(this.node);
  }
}

interface ScrapeWorkletParams {
  mixGrind: number;
  mixSparks: number;
  mixModal: number;
  drive: number;
  sparkRateMin: number;
  sparkRateMax: number;
  modalFundamental: number;
}

function scrapeWorkletParams(config: SfxConfig['scrape'], surface: ScrapeSurface): ScrapeWorkletParams {
  if (surface === 'dirt') {
    return {
      mixGrind: config.grindMix * 1.08,
      mixSparks: config.sparkMix * 0.2,
      mixModal: config.modalMix * 0.18,
      drive: config.drive * 0.35,
      sparkRateMin: config.sparkRateMin * 0.55,
      sparkRateMax: config.sparkRateMax * 0.72,
      modalFundamental: config.modalFundamental * 0.72,
    };
  }
  if (surface === 'wall') {
    return {
      mixGrind: config.grindMix * 1.18,
      mixSparks: config.sparkMix * 1.45,
      mixModal: config.modalMix * 1.35,
      drive: Math.min(1, config.drive * 1.28),
      sparkRateMin: config.sparkRateMin * 1.2,
      sparkRateMax: config.sparkRateMax * 1.15,
      modalFundamental: config.modalFundamental * 1.32,
    };
  }
  return {
    mixGrind: config.grindMix,
    mixSparks: config.sparkMix,
    mixModal: config.modalMix,
    drive: config.drive,
    sparkRateMin: config.sparkRateMin,
    sparkRateMax: config.sparkRateMax,
    modalFundamental: config.modalFundamental,
  };
}

class BoostVoice {
  private noise: AudioBufferSourceNode;
  private noiseFilter: BiquadFilterNode;
  private noiseGain: GainNode;
  private whine: OscillatorNode;
  private whineFilter: BiquadFilterNode;
  private whineGain: GainNode;
  private rumble: OscillatorNode;
  private rumbleGain: GainNode;
  private released = false;

  constructor(private readonly ctx: AudioContext, destination: AudioNode, noiseBuffer: AudioBuffer) {
    const t = ctx.currentTime;
    this.noise = ctx.createBufferSource();
    this.noise.buffer = noiseBuffer;
    this.noise.loop = true;
    this.noiseFilter = ctx.createBiquadFilter();
    this.noiseFilter.type = 'bandpass';
    this.noiseFilter.Q.value = 0.9;
    this.noiseGain = ctx.createGain();
    this.noiseGain.gain.value = 0;
    this.noise.connect(this.noiseFilter).connect(this.noiseGain).connect(destination);

    this.whine = ctx.createOscillator();
    this.whine.type = 'sawtooth';
    this.whineFilter = ctx.createBiquadFilter();
    this.whineFilter.type = 'bandpass';
    this.whineFilter.Q.value = 2.4;
    this.whineGain = ctx.createGain();
    this.whineGain.gain.value = 0;
    this.whine.connect(this.whineFilter).connect(this.whineGain).connect(destination);

    this.rumble = ctx.createOscillator();
    this.rumble.type = 'triangle';
    this.rumbleGain = ctx.createGain();
    this.rumbleGain.gain.value = 0;
    this.rumble.connect(this.rumbleGain).connect(destination);

    this.noise.start(t);
    this.whine.start(t);
    this.rumble.start(t);
  }

  set(intensity: number, cfg: SfxConfig['boost'], masterGain: number): void {
    if (this.released) return;
    const t = this.ctx.currentTime;
    const level = clamp01(intensity);
    const attack = ms(cfg.attackMs);
    const spoolHz = cfg.spoolStartHz + (cfg.spoolEndHz - cfg.spoolStartHz) * Math.pow(level, 0.75);
    const whineHz = cfg.whineStartHz + (cfg.whineEndHz - cfg.whineStartHz) * Math.pow(level, 0.9);
    this.noiseFilter.frequency.setTargetAtTime(spoolHz, t, attack);
    this.whine.frequency.setTargetAtTime(Math.max(40, whineHz * 0.45), t, attack);
    this.whineFilter.frequency.setTargetAtTime(whineHz, t, attack);
    this.rumble.frequency.setTargetAtTime(cfg.rumbleHz + level * 34, t, attack);
    this.noiseGain.gain.setTargetAtTime(masterGain * cfg.noiseGain * level, t, attack);
    this.whineGain.gain.setTargetAtTime(masterGain * cfg.whineGain * level, t, attack * 1.2);
    this.rumbleGain.gain.setTargetAtTime(masterGain * cfg.rumbleGain * level, t, attack);
  }

  release(releaseMs: number): void {
    if (this.released) return;
    this.released = true;
    const t = this.ctx.currentTime;
    const release = ms(releaseMs);
    this.noiseGain.gain.setTargetAtTime(0.0001, t, release);
    this.whineGain.gain.setTargetAtTime(0.0001, t, release);
    this.rumbleGain.gain.setTargetAtTime(0.0001, t, release);
    const stopAt = t + release * 5 + 0.08;
    this.noise.stop(stopAt);
    this.whine.stop(stopAt);
    this.rumble.stop(stopAt);
    this.rumble.addEventListener('ended', () => this.dispose(), { once: true });
  }

  dispose(): void {
    stopSource(this.noise);
    stopSource(this.whine);
    stopSource(this.rumble);
    disconnectNode(this.noise);
    disconnectNode(this.noiseFilter);
    disconnectNode(this.noiseGain);
    disconnectNode(this.whine);
    disconnectNode(this.whineFilter);
    disconnectNode(this.whineGain);
    disconnectNode(this.rumble);
    disconnectNode(this.rumbleGain);
  }
}

class SlipRaspVoice {
  private noise: AudioBufferSourceNode;
  private noiseFilter: BiquadFilterNode;
  private noiseGain: GainNode;
  private whine: OscillatorNode;
  private whineGain: GainNode;
  private body: OscillatorNode;
  private bodyGain: GainNode;
  private released = false;

  constructor(private readonly ctx: AudioContext, destination: AudioNode, noiseBuffer: AudioBuffer) {
    const t = ctx.currentTime;
    this.noise = ctx.createBufferSource();
    this.noise.buffer = noiseBuffer;
    this.noise.loop = true;
    this.noiseFilter = ctx.createBiquadFilter();
    this.noiseFilter.type = 'bandpass';
    this.noiseFilter.Q.value = 1.8;
    this.noiseGain = ctx.createGain();
    this.noiseGain.gain.value = 0;
    this.noise.connect(this.noiseFilter).connect(this.noiseGain).connect(destination);

    this.whine = ctx.createOscillator();
    this.whine.type = 'sawtooth';
    this.whineGain = ctx.createGain();
    this.whineGain.gain.value = 0;
    this.whine.connect(this.whineGain).connect(destination);

    this.body = ctx.createOscillator();
    this.body.type = 'triangle';
    this.bodyGain = ctx.createGain();
    this.bodyGain.gain.value = 0;
    this.body.connect(this.bodyGain).connect(destination);

    this.noise.start(t);
    this.whine.start(t);
    this.body.start(t);
  }

  set(intensity: number, speedHint: number, cfg: SfxConfig['slipRasp'], masterGain: number): void {
    if (this.released) return;
    const t = this.ctx.currentTime;
    const level = clamp01(intensity);
    const speed = clamp01(speedHint);
    const attack = ms(cfg.attackMs);
    const wobble = 1 + Math.sin(t * 11.7) * cfg.wobbleDepth * level;
    this.noiseFilter.Q.setTargetAtTime(cfg.filterQ, t, 0.04);
    this.noiseFilter.frequency.setTargetAtTime((cfg.filterBaseHz + cfg.filterSweepHz * speed) * wobble, t, attack);
    this.whine.frequency.setTargetAtTime((cfg.whineBaseHz + cfg.whineSweepHz * speed) * (0.92 + level * 0.14), t, attack);
    this.body.frequency.setTargetAtTime(cfg.bodyHz + speed * 55, t, attack);
    this.noiseGain.gain.setTargetAtTime(masterGain * cfg.noiseGain * level, t, attack);
    this.whineGain.gain.setTargetAtTime(masterGain * cfg.whineGain * level * speed, t, attack * 1.25);
    this.bodyGain.gain.setTargetAtTime(masterGain * cfg.bodyGain * level, t, attack);
  }

  release(releaseMs: number): void {
    if (this.released) return;
    this.released = true;
    const t = this.ctx.currentTime;
    const release = ms(releaseMs);
    this.noiseGain.gain.setTargetAtTime(0.0001, t, release);
    this.whineGain.gain.setTargetAtTime(0.0001, t, release);
    this.bodyGain.gain.setTargetAtTime(0.0001, t, release);
    const stopAt = t + release * 5 + 0.08;
    this.noise.stop(stopAt);
    this.whine.stop(stopAt);
    this.body.stop(stopAt);
    this.body.addEventListener('ended', () => this.dispose(), { once: true });
  }

  dispose(): void {
    stopSource(this.noise);
    stopSource(this.whine);
    stopSource(this.body);
    disconnectNode(this.noise);
    disconnectNode(this.noiseFilter);
    disconnectNode(this.noiseGain);
    disconnectNode(this.whine);
    disconnectNode(this.whineGain);
    disconnectNode(this.body);
    disconnectNode(this.bodyGain);
  }
}

class AirBrakeVoice {
  private noise: AudioBufferSourceNode;
  private highpass: BiquadFilterNode;
  private gain: GainNode;
  private released = false;

  constructor(private readonly ctx: AudioContext, destination: AudioNode, noiseBuffer: AudioBuffer) {
    const t = ctx.currentTime;
    this.noise = ctx.createBufferSource();
    this.noise.buffer = noiseBuffer;
    this.noise.loop = true;
    this.highpass = ctx.createBiquadFilter();
    this.highpass.type = 'highpass';
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.noise.connect(this.highpass).connect(this.gain).connect(destination);
    this.noise.start(t);
  }

  set(intensity: number, cfg: SfxConfig['airBrake'], masterGain: number): void {
    if (this.released) return;
    const t = this.ctx.currentTime;
    const level = clamp01(intensity);
    this.highpass.Q.setTargetAtTime(cfg.filterQ, t, 0.04);
    this.highpass.frequency.setTargetAtTime(
      cfg.sustainHighpassHz + (cfg.highpassStartHz - cfg.sustainHighpassHz) * level,
      t,
      ms(cfg.attackMs),
    );
    this.gain.gain.setTargetAtTime(masterGain * cfg.sustainGain * level, t, ms(cfg.attackMs));
  }

  release(releaseMs: number): void {
    if (this.released) return;
    this.released = true;
    const t = this.ctx.currentTime;
    const release = ms(releaseMs);
    this.highpass.frequency.setTargetAtTime(420, t, release);
    this.gain.gain.setTargetAtTime(0.0001, t, release);
    this.noise.stop(t + release * 5 + 0.08);
    this.noise.addEventListener('ended', () => this.dispose(), { once: true });
  }

  dispose(): void {
    stopSource(this.noise);
    disconnectNode(this.noise);
    disconnectNode(this.highpass);
    disconnectNode(this.gain);
  }
}

function stopSource(source: AudioScheduledSourceNode): void {
  try {
    source.stop();
  } catch {
    // Source may already be stopped.
  }
}

function disconnectNode(node: AudioNode): void {
  try {
    node.disconnect();
  } catch {
    // Already disconnected.
  }
}
